import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Clock, LogIn, LogOut } from "lucide-react";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, DataTable, Td, StatusBadge, Field, TextInput, ActionButton, EmptyState } from "../../components/common";

const TIME_COLUMNS = ["Driver", "Clock In", "Clock Out", "Hours", "Status", "Notes"];

export default function SwdTimekeeping() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/swd/time-entries").then((r) => setEntries(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);

  const myOpenEntry = entries.find((e) => e.driver_id === user?.id && !e.clock_out);

  const clockIn = async () => {
    setBusy(true);
    try {
      await api.post("/swd/time-entries/clock-in", notes ? { notes } : {});
      toast.success("Clocked in");
      setNotes("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const clockOut = async (entry) => {
    setBusy(true);
    try {
      await api.post(`/swd/time-entries/${entry.id}/clock-out`);
      toast.success("Clocked out");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="swd-timekeeping-page">
      <PageHeader title="Driver Timekeeping" subtitle="Clock in/out tied to drivers — hours feed payroll and job costing" testId="swd-timekeeping-header" />

      <div className="bg-card border border-slate-700/60 rounded-lg p-6 mb-6 flex flex-wrap items-end gap-4" data-testid="clock-panel">
        <div className="flex-1 min-w-[240px]">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-2">Your shift status</p>
          {myOpenEntry ? (
            <p className="text-sm text-emerald-400 font-medium">
              On the clock since {fmtDate(myOpenEntry.clock_in)}
            </p>
          ) : (
            <p className="text-sm text-slate-400">Not clocked in</p>
          )}
        </div>
        {!myOpenEntry && (
          <div className="w-64">
            <Field label="Notes (optional)">
              <TextInput testId="clockin-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Pre-trip, yard, etc." />
            </Field>
          </div>
        )}
        {myOpenEntry ? (
          <ActionButton testId="clock-out-button" variant="danger" onClick={() => clockOut(myOpenEntry)} disabled={busy}>
            <LogOut size={16} /> Clock Out
          </ActionButton>
        ) : (
          <ActionButton testId="clock-in-button" onClick={clockIn} disabled={busy}>
            <LogIn size={16} /> Clock In
          </ActionButton>
        )}
      </div>

      {entries.length === 0 ? (
        <EmptyState message="No time entries recorded" testId="time-entries-empty" />
      ) : (
        <DataTable
          testId="time-entries-table"
          columns={TIME_COLUMNS}
          rows={entries}
          renderRow={(e) => (
            <tr key={e.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`time-entry-row-${e.id}`}>
              <Td className="font-medium text-slate-100">{e.driver_name}</Td>
              <Td className="font-mono text-xs">{fmtDate(e.clock_in)}</Td>
              <Td className="font-mono text-xs">{e.clock_out ? fmtDate(e.clock_out) : "—"}</Td>
              <Td className="font-mono text-emerald-400">{e.hours != null ? e.hours.toFixed(2) : "—"}</Td>
              <Td>{e.clock_out ? <StatusBadge status="completed" /> : <StatusBadge status="active" />}</Td>
              <Td className="text-slate-500 max-w-xs truncate">{e.notes || "—"}</Td>
            </tr>
          )}
        />
      )}
    </div>
  );
}
